import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { KIND_LABELS, isCameraKind } from '@iot-dashboard/shared';
import { useAlerts, useDevice, useEvents, usePlates, useSites, useUpdateDevice } from '../../api/hooks';
import { CameraSnapshot } from '../../components/CameraSnapshot';
import { FormRow, TagsInput } from '../../components/Form';
import { KindIcon } from '../../components/KindIcon';
import { Panel } from '../../components/Panel';
import { SeverityBadge } from '../../components/SeverityBadge';
import { StatusPill } from '../../components/StatusDot';
import { ErrorState, Loading, errorMessage } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { formatDateTime, macWithColons } from '../../lib/format';
import { nameBesidePlate, plateLabel, vehicleSearch } from '../../lib/plates';
import { detectionSummary, featureSummary } from './CamerasPage';

export function CameraDetailPage() {
  const { tenantId, base, canConfigure } = useTenantContext();
  const { deviceId = '' } = useParams();
  const toast = useToast();
  const device = useDevice(tenantId, deviceId);
  const sites = useSites(tenantId);
  const alerts = useAlerts(tenantId, 'open');
  const events = useEvents(tenantId, 200);
  const update = useUpdateDevice(tenantId, deviceId);
  const plates = usePlates(tenantId, { deviceId, limit: 10 });

  const [editing, setEditing] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  if (device.isPending) return <Loading label="Loading camera…" />;
  if (device.isError) return <ErrorState error={device.error} retry={() => device.refetch()} />;

  const d = device.data;
  // Reached through a link that did not know the kind: hand it to the sensor page.
  if (!isCameraKind(d.kind)) {
    return <Navigate to={`${base}/sensors/${encodeURIComponent(d.deviceId)}`} replace />;
  }
  const c = d.camera;
  const site = sites.data?.find((s) => s.siteId === d.siteId);
  const deviceAlerts = (alerts.data ?? []).filter((a) => a.deviceId === d.deviceId);
  const deviceEvents = (events.data ?? []).filter((e) => e.deviceId === d.deviceId).slice(0, 15);
  const reachable = d.status !== 'offline' && d.status !== 'dormant';

  const startEdit = () => {
    setTags(d.tags);
    setNotes(d.notes ?? '');
    setEditing(true);
  };
  const save = async () => {
    try {
      await update.mutateAsync({ tags, notes });
      toast.push('Camera updated', 'success');
      setEditing(false);
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Cameras', to: `${base}/cameras` }, { label: d.name }]}
        title={
          <span className="row">
            <KindIcon kind={d.kind} size={22} /> {d.name} <StatusPill status={d.status} />
          </span>
        }
        subtitle={`${KIND_LABELS[d.kind]}${site ? ` · ${site.name}` : ''}`}
      />

      {deviceAlerts.length > 0 ? (
        <div
          className={`alert-banner alert-banner--${deviceAlerts.some((a) => a.severity === 'critical') ? 'critical' : 'warning'}`}
        >
          <div className="grow">
            <strong>
              {deviceAlerts.length} open alert{deviceAlerts.length === 1 ? '' : 's'}.
            </strong>{' '}
            {deviceAlerts.map((a) => a.message).join(' · ')}
          </div>
          <Link to={`${base}/alerts`} className="btn btn--sm">
            Manage alerts
          </Link>
        </div>
      ) : null}

      <div className="grid-2">
        <Panel title="Latest frame" flush>
          <CameraSnapshot
            tenantId={tenantId}
            deviceId={d.deviceId}
            name={d.name}
            unavailable={reachable ? undefined : 'This camera is offline, so there is no frame to show.'}
          />
        </Panel>

        <Panel
          title="Details"
          actions={canConfigure && !editing ? <button type="button" className="btn btn--sm" onClick={startEdit}>Edit</button> : null}
        >
          <dl className="kv">
            <dt>MAC</dt>
            <dd className="mono">{macWithColons(d.mac)}</dd>
            <dt>Firmware</dt>
            <dd>{d.firmwareVersion ?? '—'}</dd>
            <dt>Site</dt>
            <dd>{site ? <Link to={`${base}/overview?site=${encodeURIComponent(site.siteId)}`}>{site.name}</Link> : d.siteId}</dd>
            <dt>Detects</dt>
            <dd>{detectionSummary(d)}</dd>
            <dt>Audio &amp; video</dt>
            <dd>{featureSummary(d)}</dd>
            {c?.videoMode ? (<><dt>Video mode</dt><dd>{c.videoMode}</dd></>) : null}
            {c?.hdrMode ? (<><dt>HDR</dt><dd>{c.hdrMode}</dd></>) : null}
            {c?.lcdMessage ? (<><dt>Door screen</dt><dd>{c.lcdMessage}</dd></>) : null}
            <dt>Connection</dt>
            <dd>{d.connectionState ?? '—'}</dd>
            <dt>Last seen</dt>
            <dd><TimeAgo iso={d.lastSeenAt} /></dd>
            <dt>Up since</dt>
            <dd>{formatDateTime(d.upSince)}</dd>
            <dt>Last sync</dt>
            <dd><TimeAgo iso={d.lastSyncAt} /></dd>
            {d.offlineSince ? (<><dt>Offline since</dt><dd>{formatDateTime(d.offlineSince)}</dd></>) : null}
          </dl>
        </Panel>
      </div>

      <div className="grid-2">
        <Panel
          title="Tags & notes"
          actions={
            editing ? (
              <>
                <button type="button" className="btn btn--sm" onClick={() => setEditing(false)}>Cancel</button>
                <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={update.isPending}>Save</button>
              </>
            ) : null
          }
        >
          {editing ? (
            <div className="form">
              <FormRow label="Tags"><TagsInput value={tags} onChange={setTags} /></FormRow>
              <FormRow label="Notes"><textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} /></FormRow>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 8 }}>
                {d.tags.length ? d.tags.map((t) => <span key={t} className="tag">{t}</span>) : <span className="muted">No tags</span>}
              </div>
              <div className="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                {d.notes || <span className="muted">No notes</span>}
              </div>
            </>
          )}
        </Panel>

        <Panel
          title="Licence plates"
          flush
          actions={<Link to={`${base}/plates?camera=${encodeURIComponent(d.deviceId)}`} className="small">All plates</Link>}
        >
          {(plates.data?.items.length ?? 0) === 0 ? (
            <div className="empty">
              <p>
                {c?.smartDetectTypes?.includes('licensePlate')
                  ? 'This camera detects plates, but none have reached the dashboard yet. Its console needs an Alarm Manager rule pointed at this tenant\'s webhook.'
                  : 'No plates read by this camera.'}
              </p>
            </div>
          ) : (
            <ul className="list">
              {(plates.data?.items ?? []).map((p) => (
                <li key={p.detectionId} className="list__item">
                  <span className="list__time"><TimeAgo iso={p.ts} /></span>
                  <span className="grow">
                    <Link
                      to={`${base}/plates?plate=${encodeURIComponent(vehicleSearch(p))}`}
                      className={p.plateSource === 'name' ? 'plate plate--name' : 'plate'}
                    >
                      {plateLabel(p)}
                    </Link>
                    {nameBesidePlate(p) ? <span className="cell-sub">{nameBesidePlate(p)}</span> : null}
                    {p.known === false ? <span className="cell-sub">Not on a list</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid-2">
        <Panel title="Recent events" flush>
          {deviceEvents.length === 0 ? (
            <div className="empty"><p>Nothing recorded for this camera yet.</p></div>
          ) : (
            <ul className="list">
              {deviceEvents.map((e) => (
                <li key={e.eventId} className="list__item">
                  <span className="list__time"><TimeAgo iso={e.ts} /></span>
                  <span className="grow">
                    {e.severity && e.severity !== 'info' ? <SeverityBadge severity={e.severity} /> : null} {e.title}
                    <div className="cell-sub">{e.source}</div>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}
